import { useCallback, useMemo, useContext } from 'react'
import { useIntl } from 'react-intl'
import { addToCheckoutCart } from 'actions/CheckoutActions'
import { generateBNBLCheckoutItem } from 'lib/checkout/accommodation/cart'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { useBnblExtraGuestSurcharges } from 'hooks/OfferPage/useBnblExtraGuestSurcharges'
import { applyBnblUpfrontSurchargeToCheckoutItems, isBnblV2FlashOffer, shouldShowBnblExtraGuestSurcharge } from 'lib/checkout/bnblExtraGuestSurcharge'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { useAppDispatch } from 'hooks/reduxHooks'
import TextButton from 'LuxKit/Button/TextButton'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { getBnblPackageViewsTotalPrice } from 'selectors/offerPage/offerPageSelectors'
import type { BnblTotalPackagesPrices } from 'components/OfferPage/BnblModal/utils'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import Group from 'components/utils/Group'
import { addToCart } from 'analytics/snowplow/events'
import { trackEvent } from 'analytics/analytics'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import GeoContext from 'contexts/geoContext'
import CurrencyContext from 'contexts/currencyContext'
import ModalContext from 'contexts/ModalContext'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'

interface MappedProps {
  bnblModalPackages: Array<App.BnblModal>
  totalRoomsPrice: BnblTotalPackagesPrices
  canViewLuxPlusBenefits: boolean
}

interface Props {
  offer: App.Offer
  isGift: boolean
}

function BnblPackagePricing(props: Props & MappedProps) {
  const {
    bnblModalPackages,
    totalRoomsPrice,
    offer,
    isGift,
    canViewLuxPlusBenefits,
  } = props

  const intl = useIntl()
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  // Currency is the page's, not the region default — see best-practice-conventions.md.
  const currentCurrency = useContext(CurrencyContext)

  // BNBL v2 is flash-only — svc-order rejects a v2 payload for any other offer type — so gate on the
  // flag AND the flash offer type. This single guard drives quoting, CTA state/display, and cart
  // augmentation, keeping tactical/always-on (and every other) offer on the legacy path.
  const bnblV2Enabled = isBnblV2FlashOffer(useFeatureFlag(OptimizelyFeatureFlags.bnblV2), offer.type)
  const extraGuestSurcharges = useBnblExtraGuestSurcharges(offer, bnblModalPackages, currentCurrency, bnblV2Enabled)
  // Full extra-guest surcharge the guest faces — the LE-charged portion plus the portion payable at
  // the property — so the modal total matches checkout and the payable-at-property amount isn't a
  // surprise that only appears at checkout.
  const extraGuestSurchargeTotal = extraGuestSurcharges.total + extraGuestSurcharges.payableAtProperty
  const showExtraGuestSurcharge = shouldShowBnblExtraGuestSurcharge(bnblV2Enabled, extraGuestSurchargeTotal, offer.surchargePaidDirectToVendor)
  // svc-order re-quotes and rejects a mismatched amount, so hold the CTA until the quote resolves
  // rather than charging a stale/zero surcharge.
  const awaitingSurchargeQuote = bnblV2Enabled && !extraGuestSurcharges.isReady

  const { hotelValue, hotelPrice, priceFrom, shouldDisplayValue, hotelMemberPrice, memberPriceFrom } = totalRoomsPrice
  const packageView = bnblModalPackages?.[0].packageView
  const shouldShowValue = hotelValue > 0 && hotelValue > (memberPriceFrom || priceFrom) && shouldDisplayValue
  const valueOffPercent = getValueOffPercent(hotelValue, (!isGift && canViewLuxPlusBenefits ? hotelMemberPrice : hotelPrice))
  const shouldShowRate = valueOffPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const onBuyNow = useCallback(() => {
    // Defensive: if neither the patterns call nor the single-occupancy fallback could price a
    // selected occupancy, don't proceed with a wrong amount — route the customer to support.
    if (bnblV2Enabled && extraGuestSurcharges.unpriceable) {
      showModal(<RequestAgentCallbackModal type="HOTELS_AND_VILLAS" />)
      return
    }
    // TODO: isGift cannot work with multiple cart items
    const checkoutItems = bnblModalPackages.flatMap(modalPackage => generateBNBLCheckoutItem(offer, modalPackage.packageView.package, modalPackage.occupancies))
    // perRoom is in the same flat (package, then occupancy) order as the items above, so index-align
    // them. Non-flash offers get bnblV2Enabled=false → items pass through as the legacy payload.
    const checkoutItemsWithSurcharge = applyBnblUpfrontSurchargeToCheckoutItems(checkoutItems, extraGuestSurcharges.perRoom, bnblV2Enabled)
    dispatch(addToCheckoutCart(checkoutItemsWithSurcharge, { isGift }))
    trackEvent(addToCart())
    dispatch(navigateToCheckout())
  }, [isGift, offer, bnblModalPackages, dispatch, extraGuestSurcharges.perRoom, extraGuestSurcharges.unpriceable, bnblV2Enabled, showModal])

  const { currentRegionCode } = useContext(GeoContext)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, {
      price: priceFrom,
      memberPrice: memberPriceFrom,
      packageView,
      skip: isGift,
      currentRegionCode,
      nights: packageView?.package?.duration,
    }),
  ], [offer, packageView, isGift, memberPriceFrom, priceFrom, currentRegionCode])

  return (
    <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
      <VerticalSpacer gap={16}>
        {!isGift && <LoyaltyPointsEarnWithBreakdownModal calculationRequests={pointsEarnCalculationRequests} calculationType="exact" />}
        <div>
          <PriceRowPriceCaption>{intl.formatMessage({
            id: 'nyPCBJvO8OP2gXJr80Qf',
            defaultMessage: 'From',
            description: 'Pricing label prefix before BNBL package total price',
          })}
          </PriceRowPriceCaption>
          <LuxPlusPriceStack
            price={priceFrom + (showExtraGuestSurcharge ? extraGuestSurchargeTotal : 0)}
            memberPrice={!isGift && memberPriceFrom > 0 ? memberPriceFrom + (showExtraGuestSurcharge ? extraGuestSurchargeTotal : 0) : 0}
            size="S"
            saleUnit="total"
            offerConfig={offer.luxPlus}
          />
          {shouldShowValue && <PriceRowValueDiscountWithCaption
            size="M"
            originalValue={hotelValue}
            discountPercentage={shouldShowRate ? valueOffPercent : undefined}
          />}
          <PriceRowTaxesAndFees />
        </div>
      </VerticalSpacer>
      <TextButton data-testid="bnbl-buy-btn" kind="primary" onClick={onBuyNow} size="large" disabled={awaitingSurchargeQuote}>
        {intl.formatMessage({
          id: 'fTb_L_RpGhNq2FcuNUW4',
          defaultMessage: 'Buy now',
          description: 'Button to purchase BNBL package and proceed to checkout',
        })}
      </TextButton>
    </Group>
  )
}

export default withOfferPageAndReduxState((offerPageState, appState: App.State, ownProps: Props): MappedProps => {
  return {
    totalRoomsPrice: getBnblPackageViewsTotalPrice(offerPageState, appState),
    bnblModalPackages: appState.offer.bnblModal[ownProps.offer.id],
    canViewLuxPlusBenefits: getCanViewLuxPlusBenefits(appState),
  }
}, BnblPackagePricing)
